import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit } from '@angular/core';
import { TagEditorComponent, TagEditorItem } from '@tourfold/libraries/shared-ui';
import { SystemTagsCache } from './system-tags-cache.service';

/**
 * Read-only renderer for a Tags relation. Resolves each tag ID in `value` against the shared
 * [SystemTagsCache] and renders the result as colored chips via `tf-tag-editor`'s display mode.
 *
 * Mounted by `tf-field-renderer` via {@link AttributeComponentRegistry.resolve} when a relation
 * field's `targetDefinitionSlug` matches the registered Tags system slug. Always emits zero
 * chips for an empty or missing value — no fallback dash, no placeholder text.
 *
 * The matching cardinality MANY input contract uses `string[]`; we also accept a single `string`
 * (cardinality ONE) and a nullish value (no selection) so the renderer is robust to whichever
 * shape the relation pipeline passes through.
 */
@Component({
  selector: 'tf-tags-relation-display',
  standalone: true,
  imports: [TagEditorComponent],
  templateUrl: './tags-relation-display.component.html',
  styleUrl: './tags-relation-display.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsRelationDisplayComponent implements OnInit {
  private readonly cache = inject(SystemTagsCache);

  readonly value = input<unknown>();

  /**
   * Selected tag ids derived from the polymorphic `value` input. Stored as a stable readonly
   * array so the template doesn't reallocate on unrelated change-detection ticks.
   */
  private readonly selectedIds = computed<readonly string[]>(() => normalizeIds(this.value()));

  protected readonly selectedTags = computed<readonly TagEditorItem[]>(() => {
    const ids = this.selectedIds();
    if (ids.length === 0) return [];
    const byId = new Map<string, TagEditorItem>();
    for (const t of this.cache.tags()) {
      if (t.id === undefined) continue;
      byId.set(t.id, { id: t.id, name: t.name ?? '', color: t.color ?? '#9CA3AF' });
    }
    return ids.map((id) => byId.get(id)).filter((t): t is TagEditorItem => t !== undefined);
  });

  ngOnInit(): void {
    // Fire-and-forget cache fill; rendering reacts via the computed once tags arrive.
    void this.cache.ensureLoaded();
  }
}

function normalizeIds(value: unknown): readonly string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string') return value.length > 0 ? [value] : [];
  return [];
}
